import { useEffect } from 'react';
import Bowser from 'bowser';
import { omitBy } from 'lodash-es';
import { logger } from '@zep/core-logger';
import { useLocale } from '@/hooks/useLocale';
import { useCountryCode } from '@/shared/hooks';
import { readCdnServerTiming } from '@/shared/logger/cdnServerTiming';
import { getStoredQuizTrackingUserProperties } from '@/shared/trackingContext';
import { getApplicationSessionId, getBrowserSessionId, getDeviceId } from '@/utils/common';
type Props = {
  /** withSsrLogging 이 props 로 내려보낸 SSR traceId. 서버 트레이스와의 조인 키. */
  ssrTraceId?: string;
};
export const QuizAppLoggerInitializer = ({
  ssrTraceId
}: Props) => {
  const {
    locale
  } = useLocale();
  const {
    countryCode
  } = useCountryCode();
  useEffect(() => {
    const applicationSessionId = getApplicationSessionId();
    const browserSessionId = getBrowserSessionId();
    const deviceId = getDeviceId();

    // UTM은 shared/logger 모듈 로드 시점에 이미 logger state로 동기 주입됨.
    // 여기서는 user property 기록을 위해 저장된 값을 다시 읽어 전달만 한다.
    const {
      browser,
      os
    } = Bowser.parse(window.navigator.userAgent);
    logger.setState({
      zep_application_session_id: applicationSessionId,
      zep_browser_session_id: browserSessionId,
      zepApplicationSessionId: applicationSessionId,
      zepBrowserSessionId: browserSessionId,
      zepDeviceId: deviceId
    });

    // CF Server-Timing — 문서가 CDN 캐시에서 왔는지 + 요청 ID(cdn-rid).
    // rid 는 서버 로그의 cf_rid(x-amz-cf-id)와 같은 값이라 MISS 요청의 조인 키.
    const cdnTiming = readCdnServerTiming();
    if (cdnTiming) {
      logger.setState(cdnTiming);
    }
    logger.setUserProperties({
      ...omitBy(getStoredQuizTrackingUserProperties(), v => !v),
      zep_user_agent: window.navigator.userAgent,
      zep_browser_name: browser.name,
      zep_browser_version: browser.version,
      zep_os_name: os.name,
      zep_os_version: os.version
    });
    logger.setTag('zep_application_session_id', applicationSessionId);
    logger.setTag('zep_browser_session_id', browserSessionId);
  }, []);
  useEffect(() => {
    logger.setUserProperties({
      zep_country_code: countryCode,
      zep_locale: locale
    });
  }, [countryCode, locale]);

  // 브라우저 span 에 SSR traceId 를 심어 서버 트레이스와 조인한다.
  // 문서 요청엔 traceparent 를 실을 수 없어 부모-자식 대신 조인 키로 잇는다.
  // setState 는 병합이라 값이 없을 때도 비워 써야 한다 — 안 그러면 클라 라우팅으로
  // trace 없는 페이지에 가도 직전 값이 남아 엉뚱한 요청에 붙는다.
  useEffect(() => {
    logger.setState({
      zep_ssr_trace_id: ssrTraceId ?? ''
    });
  }, [ssrTraceId]);
  return null;
};